import { FC } from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { YardstickAutoFix } from '@internal/yardstick-common';
import { AutofixV2Component } from './autofix-v2-component';
import { AutoRemediationButton } from './auto-remediation-button';

const useStyles = makeStyles<Theme>(theme => ({
  container: {
    padding: theme.spacing(3),
    paddingTop: '8px', // Match table cell padding (8px) for seamless alignment
    paddingRight: '8px', // Match table cell padding (8px) for button alignment
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(227, 245, 253, 0.04)' : '#F5F5F5',
    borderRadius: '8px',
    position: 'relative',
    // Smooth transition for the entire container
    opacity: 1,
    transform: 'translateY(0)',
    transition: theme.transitions.create(['opacity', 'transform'], {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    position: 'relative',
    minHeight: '36px', // Match button height to prevent layout shift
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginTop: '14px',
    paddingTop: 0, // No padding - let natural alignment work
  },
  title: {
    fontSize: '26px',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: '1.2', // Tighter line height for better visual alignment
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.5),
  },
  buttonWrapper: {
    flexShrink: 0, // Prevent button from shrinking
    display: 'inline-block', // Ensure it behaves like the collapsed state
    paddingRight: '8px',
  },
  content: {
    marginTop: theme.spacing(2),
    paddingRight: '16px',
  },
}));

interface AutofixV2LogicalAccordionComponentProps {
  issueId: string;
  issueTitle: string;
  autofixConfig: YardstickAutoFix;
  remediationSteps?: string;
  entityRef?: string;
  checkId?: string;
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * AutoFix V2 Logical Accordion Component
 *
 * This component is designed specifically for use in LogicalAccordion tables.
 * It replaces the table row when expanded and shows:
 * - A header with "Auto Remediation" title and finding subtitle
 * - A collapse button in the top right
 * - The AutofixV2Component for the actual autofix functionality
 */
export const AutofixV2LogicalAccordionComponent: FC<
  AutofixV2LogicalAccordionComponentProps
> = ({
  issueId,
  issueTitle,
  autofixConfig,
  remediationSteps,
  entityRef,
  checkId,
  isExpanded,
  onToggle,
}) => {
  const classes = useStyles();

  if (!isExpanded) {
    return null;
  }
  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box className={classes.titleSection}>
          <Typography className={classes.title}>Auto Remediation</Typography>
          <Typography className={classes.subtitle}>
            <span style={{ fontWeight: 'bold' }}>Finding: </span>
            {issueTitle}
          </Typography>
        </Box>
        <Box className={classes.buttonWrapper}>
          <AutoRemediationButton isExpanded={isExpanded} onClick={onToggle} />
        </Box>
      </Box>
      <Box className={classes.content}>
        <AutofixV2Component
          issueId={issueId}
          autofixConfig={autofixConfig}
          remediationSteps={remediationSteps}
          entityRef={entityRef}
          checkId={checkId}
          isGroupedIssue
          forceExpandPreview
        />
      </Box>
    </Box>
  );
};
